import { alpha, Box, Chip, Tooltip, useTheme } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

const formatDateTimeDisplay = (value) => {
    const raw = String(value || '').trim();
    if (!raw || raw === '-') return '-';

    const normalizedIso = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z)?$/);
    if (normalizedIso) return `${normalizedIso[1]} ${normalizedIso[2]}`;

    const isoLikeDateTime = raw.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?Z?$/);
    if (isoLikeDateTime) return `${isoLikeDateTime[1]} ${isoLikeDateTime[2]}`;

    return raw;
};

const getBaseRowAllowAllDisplay = (value) => {
    const raw = String(value ?? '').trim();
    return raw === '-' ? 'allow all' : value;
};

export default function DataGridData({ rows, onRowsChange }) {

    const theme = useTheme();

    const getBaseRowId = (row) => String(row?.base_row_id || row?.id || '');

    const selectableBaseRowIds = useMemo(() => {
        const ids = new Set();

        (rows || []).forEach((row) => {
            if (row?.is_base_row === false) {
                const baseRowId = getBaseRowId(row);
                if (baseRowId) {
                    ids.add(baseRowId);
                }
            }
        });

        return ids;
    }, [rows]);

    const hasSelectableBaseRows = selectableBaseRowIds.size > 0;

    const rowSelectionModel = useMemo(
        () => ({
            type: 'include',
            ids: new Set(
                (rows || [])
                    .filter((row) => row?.is_base_row === true && row?.checked && selectableBaseRowIds.has(getBaseRowId(row)))
                    .map((row) => getBaseRowId(row))
            ),
        }),
        [rows, selectableBaseRowIds]
    );

    const handleRowSelectionChange = (newRowSelectionModel) => {
        if (typeof onRowsChange !== 'function') return;

        const selectedIdSet = new Set(Array.from(newRowSelectionModel?.ids || []));
        const isExcludeModel = newRowSelectionModel?.type === 'exclude';

        const nextRows = (rows || []).map((row) => {
            const baseRowId = getBaseRowId(row);
            const hasSelectableBaseId = selectableBaseRowIds.has(baseRowId);
            const isBaseRowSelected = hasSelectableBaseId
                && (isExcludeModel ? !selectedIdSet.has(baseRowId) : selectedIdSet.has(baseRowId));

            const isChecked = (row?.is_base_row === true)
                ? isBaseRowSelected
                : hasSelectableBaseId && isBaseRowSelected;

            return {
                ...row,
                checked: isChecked,
            };
        });

        onRowsChange(nextRows);
    };

    const columns = useMemo(() => [
        {
            field: 'resource_name',
            headerName: 'Data',
            width: 200,
            renderCell: (params) => {
                const profileName = String(params.row?.profile_name || '').trim();
                if (profileName && profileName !== '-') {
                    return <>
                        <SubdirectoryArrowRightIcon sx={{ fontSize: 12, mr: 0.25 }} />
                        <span style={{ fontWeight: 400 }}>Profile: {profileName}</span>
                    </>;
                }
                return <span style={{ fontSize: 13, fontWeight: 600, color: theme.palette.primary.main }}>{params.value}</span>;
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 80,
            renderCell: (params) => {
                if (params.row?.is_base_row === true) {
                    return <span style={{ fontSize: 13, fontWeight: 600, color: theme.palette.primary.main }}>{params.value}</span>;
                }
                return <span style={{ fontSize: 12, fontWeight: 400 }}>{params.value}</span>;
            },
        },
        {
            field: 'blank_1',
            headerName: '',
            width: 200,
        },
        {
            field: 'environment_resource_name',
            headerName: 'Incoming Resource',
            width: 170,
            renderCell: (params) => {
                if (params.row?.is_base_row === true) {
                    return <span style={{ fontSize: 13, fontWeight: 600, color: theme.palette.primary.main }}>{getBaseRowAllowAllDisplay(params.value)}</span>;
                }
                return <span style={{ fontSize: 12, fontWeight: 400 }}>{params.value}</span>;
            },
        },
        {
            field: 'environment_ip_address',
            headerName: 'Incoming IP Address',
            width: 150,
            renderCell: (params) => {
                if (params.row?.is_base_row === true) {
                    return <span style={{ fontSize: 13, fontWeight: 600, color: theme.palette.primary.main }}>{getBaseRowAllowAllDisplay(params.value)}</span>;
                }
                return <span style={{ fontSize: 12, fontWeight: 400 }}>{params.value}</span>;
            },
        },
        {
            field: 'environment_date_time',
            headerName: 'Incoming Date Time',
            width: 150,
            renderCell: (params) => {
                if (params.row?.is_base_row === true) {
                    return <span style={{ fontSize: 13, fontWeight: 600, color: theme.palette.primary.main }}>{getBaseRowAllowAllDisplay(formatDateTimeDisplay(params.value))}</span>;
                }
                return <span style={{ fontSize: 12, fontWeight: 400 }}>{formatDateTimeDisplay(params.value)}</span>;
            },
        },
        {
            field: 'result',
            headerName: 'Result (Decision)',
            width: 120,
            renderCell: (params) => {
                const value = params.value;
                let color = '';
                if (value === 'Permit') {
                    color = 'green';
                } else if (value === 'Deny') {
                    color = 'red';
                } else if (value === 'Inapplicable') {
                    color = 'orange';
                } else if (value === 'Indeterminate') {
                    color = 'gray';
                }
                return (
                    <span style={{ color: color, fontWeight: 'bold' }}>
                        {value}
                    </span>
                );
            }
        },
        {
            field: 'reason',
            headerName: 'Reason',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                const reasonList = Array.isArray(params.value) ? params.value : [];
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                        {(reasonList.map((reason, index) => {
                            return (
                                <Tooltip title={reason?.message || ''} arrow key={index}>
                                    <Chip label={reason?.type} sx={{ height: '25px', backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }} />
                                </Tooltip>
                            )
                        }))}
                    </Box>
                )
            },
        },
    ], []);

    return (
        <Box sx={{ width: '100%' }}>
            <DataGridPremium
                rows={rows}
                columns={columns}
                autoHeight
                checkboxSelection
                isRowSelectable={(params) => params?.row?.is_base_row === true && selectableBaseRowIds.has(getBaseRowId(params?.row))}
                getRowClassName={(params) => {
                    const isSelectableBaseRow = params?.row?.is_base_row === true && selectableBaseRowIds.has(getBaseRowId(params?.row));
                    return isSelectableBaseRow ? '' : 'row-no-checkbox';
                }}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionChange}
                disableRowSelectionOnClick
                hideFooter
                rowHeight={38}
                columnHeaderHeight={34}
                sx={{
                    borderRadius: 0,
                    fontSize: 12,
                    '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root, & .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
                        padding: '4px',
                    },
                    '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root .MuiSvgIcon-root, & .MuiDataGrid-cellCheckbox .MuiCheckbox-root .MuiSvgIcon-root': {
                        fontSize: 18,
                    },
                    ...(!hasSelectableBaseRows && {
                        '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                            display: 'none',
                        },
                    }),
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#eaf4ff",
                        borderBottom: "2px solid #1e9be8",
                    },

                    "& .MuiDataGrid-columnHeader": {
                        backgroundColor: "#eaf4ff",
                        color: "#555",
                        fontWeight: 600,
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #dddddd",
                        backgroundColor: "#ffffff",
                        outline: "none !important",
                        display: "flex",
                        alignItems: "center",
                    },
                    "& .MuiDataGrid-row": {
                        backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {
                        backgroundColor: "#ffffff !important",
                    },
                    "& .row-no-checkbox .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
                        display: "none",
                    },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                        outline: "none",
                    },
                }}
            />
        </Box>
    );
}